import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginModal = ({ isOpen, onClose }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login'); // 'login', 'register', 'forgot'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    whatsapp: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let result;
      if (mode === 'login') {
        result = await login(formData.email, formData.password);
        if (result.success) {
          toast.success('Login realizado com sucesso!');
          onClose();
          setFormData({ email: '', password: '', name: '', whatsapp: '' });
        } else {
          toast.error(result.error);
        }
      } else if (mode === 'register') {
        result = await register(formData.name, formData.email, formData.password, formData.whatsapp);
        if (result.success) {
          toast.success('Cadastro realizado com sucesso!');
          onClose();
          setFormData({ email: '', password: '', name: '', whatsapp: '' });
        } else {
          toast.error(result.error);
        }
      } else if (mode === 'forgot') {
        // Enviar email de recuperação de senha
        const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
          email: formData.email
        });
        if (response.data.success) {
          toast.success('Email de recuperação enviado! Verifique sua caixa de entrada.');
          setMode('login');
        }
      }
    } catch (error) {
      if (mode === 'forgot') {
        toast.error(error.response?.data?.detail || 'Erro ao enviar email de recuperação');
      } else {
        toast.error('Erro ao processar solicitação');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm overflow-y-auto p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full mx-4 my-8 relative max-h-[calc(100vh-4rem)] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">
            {mode === 'login' ? 'Entrar' : mode === 'register' ? 'Criar Conta' : 'Recuperar Senha'}
          </h2>
          <p className="text-gray-400">
            {mode === 'login' ? 'Acesse sua conta para continuar' : 
             mode === 'register' ? 'Preencha os dados para criar sua conta' :
             'Digite seu email para receber o link de recuperação'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <div>
              <Label htmlFor="name" className="text-white">Nome Completo</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-black border-zinc-700 text-white mt-2"
                required={mode === 'register'}
                disabled={isSubmitting}
                data-testid="register-name-input"
              />
            </div>
          )}

          <div>
            <Label htmlFor="email" className="text-white">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="bg-black border-zinc-700 text-white mt-2"
              required
              disabled={isSubmitting}
              data-testid="login-email-input"
            />
          </div>

          {mode === 'register' && (
            <div>
              <Label htmlFor="whatsapp" className="text-white flex items-center gap-2">
                <Phone size={16} className="text-green-500" />
                WhatsApp
              </Label>
              <Input
                id="whatsapp"
                type="tel"
                placeholder="(11) 99999-9999"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                className="bg-black border-zinc-700 text-white mt-2"
                disabled={isSubmitting}
                data-testid="register-whatsapp-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Receba informações sobre suas aulas via WhatsApp
              </p>
            </div>
          )}

          {mode !== 'forgot' && (
            <div>
              <Label htmlFor="password" className="text-white">Senha</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-black border-zinc-700 text-white mt-2"
                required
                disabled={isSubmitting}
                data-testid="login-password-input"
              />
            </div>
          )}

          {mode === 'login' && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => setMode('forgot')}
                className="text-red-500 hover:text-red-400 transition-colors text-sm"
                disabled={isSubmitting}
              >
                Esqueceu sua senha?
              </button>
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full bg-red-600 hover:bg-red-700 text-white"
            disabled={isSubmitting}
            data-testid="auth-submit-button"
          >
            {isSubmitting ? 'Processando...' : 
             mode === 'login' ? 'Entrar' : 
             mode === 'register' ? 'Cadastrar' : 
             'Enviar Link de Recuperação'}
          </Button>
        </form>

        <div className="mt-6 text-center space-y-2">
          {mode === 'forgot' ? (
            <button
              onClick={() => setMode('login')}
              className="text-gray-400 hover:text-red-500 transition-colors text-sm"
              disabled={isSubmitting}
            >
              Voltar ao login
            </button>
          ) : (
            <button
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-gray-400 hover:text-red-500 transition-colors text-sm"
              disabled={isSubmitting}
            >
              {mode === 'login' ? 'Não tem uma conta? Cadastre-se' : 'Já tem conta? Faça login'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LoginModal;